import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDashboardQuery,
  useLazyDashboardSection,
  usePayloadOptimization,
} from './useDashboardOptimization';
import { VirtualizedList } from './VirtualizedList';
import { SkeletonDashboardMetrics } from './Skeleton';

interface DashboardSectionProps {
  title: string;
  children: React.ReactNode;
  lazy?: boolean;
  priority?: 'high' | 'medium' | 'low';
  className?: string;
  rootMargin?: string;
  isEmpty?: boolean;
  hideIfEmpty?: boolean;
}

/**
 * Section de dashboard optimisée avec lazy loading
 */
export const DashboardSection: React.FC<DashboardSectionProps> = ({
  title,
  children,
  lazy = false,
  priority = 'medium',
  className = '',
  rootMargin = '50px',
  isEmpty = false,
  hideIfEmpty = false,
}) => {
  const { ref, loading, hasLoaded } = useLazyDashboardSection(() => Promise.resolve(children), {
    enabled: lazy,
    rootMargin,
  });

  const priorityClasses = {
    high: 'dashboard-section',
    medium: 'dashboard-section',
    low: 'opacity-80',
  };

  if (hideIfEmpty && isEmpty) {
    return null;
  }

  if (lazy && !hasLoaded) {
    return (
      <div ref={ref} className={`p-6 bg-white rounded-lg border ${className}`}>
        <h3 className="text-lg font-bold mb-4">{title}</h3>
        <div className="animate-pulse">
          <div className="h-32 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`dashboard-section p-6 bg-white rounded-lg border ${priorityClasses[priority]} ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">{title}</h3>
      </div>
      {loading ? (
        <div className="animate-pulse">
          <div className="h-32 bg-slate-200 rounded"></div>
        </div>
      ) : (
        children
      )}
    </div>
  );
};

/**
 * Composant de liste optimisée pour réduire les re-renders
 */
interface OptimizedListProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  itemHeight?: number;
  containerHeight?: number;
  virtualize?: boolean;
  emptyState?: string | React.ReactNode;
}

const OptimizedListComponent = <T extends { id: string | number }>({
  items,
  renderItem,
  itemHeight = 80,
  containerHeight = 300,
  virtualize = false,
  emptyState,
}: OptimizedListProps<T>) => {
  const { t } = useTranslation('common');
  const resolvedEmptyState = emptyState !== undefined ? emptyState : t('dashboardOptimizer.emptyState');
  const memoizedItems = useMemo(() => items, [items]);

  const renderedItems = useMemo(
    () =>
      memoizedItems.map((item) => (
        <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
      )),
    [memoizedItems, renderItem]
  );

  if (memoizedItems.length === 0) {
    return (
      <div className="py-12 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
        <div className="text-sm font-medium italic">{resolvedEmptyState}</div>
      </div>
    );
  }

  if (virtualize && memoizedItems.length > 10) {
    return (
      <VirtualizedList
        items={memoizedItems}
        itemHeight={itemHeight}
        containerHeight={containerHeight}
        renderItem={renderItem}
      />
    );
  }

  return <div className="space-y-4">{renderedItems}</div>;
};

export const OptimizedList = React.memo(OptimizedListComponent) as <
  T extends { id: string | number },
>(
  props: OptimizedListProps<T>
) => JSX.Element;

interface DashboardMetricsProps<TData = unknown> {
  queryKey: string[];
  queryFn: () => Promise<TData>;
  render: (data: TData) => React.ReactNode;
  cacheTime?: number;
  enableBackgroundRefetch?: boolean;
}

/**
 * Métriques de dashboard avec cache optimisé
 */
export const DashboardMetrics = <TData,>({
  queryKey,
  queryFn,
  render,
  cacheTime = 2 * 60 * 1000,
  enableBackgroundRefetch = false,
}: DashboardMetricsProps<TData>) => {
  const { t } = useTranslation('common');
  const { data, isLoading, error } = useDashboardQuery<TData>(queryKey, queryFn, {
    cacheTime,
    enableBackgroundRefetch,
    payloadReduction: true,
  });

  if (isLoading) {
    return <SkeletonDashboardMetrics />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">{t('dashboardOptimizer.metricsError')}</p>
      </div>
    );
  }

  return <>{data && render(data)}</>;
};

/**
 * Hook pour optimiser les performances globales du dashboard
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useDashboardPerformance() {
  const { optimizePayload, cacheOptimizedQuery, getCachedQuery } = usePayloadOptimization();

  const optimizeQuery = useMemo(
    () => ({
      // Réduire le payload pour les listes
      listFields: ['id', 'name', 'status', 'priority', 'createdAt'],
      // Réduire le payload pour les métriques
      metricsFields: ['count', 'total', 'average', 'status'],
      // Cache optimisé
      cacheOptimizedQuery,
      getCachedQuery,
    }),
    [cacheOptimizedQuery, getCachedQuery]
  );

  return {
    optimizePayload,
    ...optimizeQuery,
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePerformanceMetrics() {
  const [metrics, setMetrics] = React.useState({
    renderTime: 0,
    apiCalls: 0,
    cacheHits: 0,
    payloadSize: 0,
  });

  const measureRenderTime = React.useCallback((startTime: number) => {
    const renderTime = performance.now() - startTime;
    setMetrics((prev) => ({ ...prev, renderTime }));
  }, []);

  const trackApiCall = React.useCallback(() => {
    setMetrics((prev) => ({ ...prev, apiCalls: prev.apiCalls + 1 }));
  }, []);

  const trackCacheHit = React.useCallback(() => {
    setMetrics((prev) => ({ ...prev, cacheHits: prev.cacheHits + 1 }));
  }, []);

  const trackPayloadSize = React.useCallback((size: number) => {
    setMetrics((prev) => ({ ...prev, payloadSize: size }));
  }, []);

  return {
    metrics,
    measureRenderTime,
    trackApiCall,
    trackCacheHit,
    trackPayloadSize,
  };
}
